// Copyright (c) 2026 PGNS LLC

import { Link } from '@tanstack/react-router';
import { Skeleton } from '@pgns/ui/skeleton';
import { ComponentList } from '@/components/component-list';
import { DataUnavailable } from '@/components/data-unavailable';
import { IncidentTimeline } from '@/components/incident-timeline';
import { OverallStatus } from '@/components/overall-status';
import { useStatusData } from '@/hooks/use-status-data';
import { useNow } from '@/hooks/use-now';

function LoadingState() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-72" />
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-28 w-full" />
    </div>
  );
}

/**
 * The current-status page.
 *
 * Ordered by what a reader in an outage needs: overall verdict, then any open
 * incident, then per-component detail. Recent history is a link, not a wall.
 */
export function OverviewPage() {
  const { summary, history, incidents, loading, error, lastLoadedAt, refresh } = useStatusData();
  const now = useNow();
  const recent = (incidents?.incidents ?? []).filter((incident) => incident.resolved).slice(0, 3);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-8">
      {error !== null && <DataUnavailable stale={summary !== null} />}
      {summary === null ? (
        <p className="text-sm text-muted-foreground">
          No status data has been retrieved yet. The public JSON API is at{' '}
          <code className="font-mono">/v1/summary</code>.
        </p>
      ) : (
        <>
          <OverallStatus
            summary={summary}
            lastLoadedAt={lastLoadedAt}
            now={now}
            onRefresh={refresh}
          />
          {summary.active_incidents.length > 0 && (
            <section className="space-y-4">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                Active incidents
              </h2>
              <IncidentTimeline
                incidents={summary.active_incidents}
                emptyMessage="No active incidents."
              />
            </section>
          )}
          <ComponentList components={summary.components} history={history} now={now} />
        </>
      )}
      {recent.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            Recently resolved
          </h2>
          <IncidentTimeline incidents={recent} compact emptyMessage="No recent incidents." />
          <Link to="/incidents" className="text-sm text-primary hover:underline">
            All incident history
          </Link>
        </section>
      )}
    </div>
  );
}
